import { FC } from 'react';
import classNames from 'classnames';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { CreateReelVideoSvg } from '@/Components/Icons/CreateReelVideoSvg';
import { CreateReelAudioSvg } from '@/Components/Icons/CreateReelAudioSvg';
import { CreateReelScriptSvg } from '@/Components/Icons/CreateReelScriptSvg';
import { ChevronRightSvg } from '@/Components/Icons/ChevronRightSvg';

type CreateReelModalProps = AppModalProps<{}>;

export const CreateReelModal: FC<CreateReelModalProps> = ({
    payload,
    onClose,
}) => {
    const options = [
        {
            isStandIn: false,
            title: 'Upload a video file',
            text: 'Upload a video recording to your Video Reel Library.',
            bgClassName: 'group-hover:scale-125 scale-150',
            contentClassName: 'group-hover:scale-110',
            bgElement: (
                <img
                    src="/img/shade-bg-1.svg"
                    className="w-full h-full object-cover "
                />
            ),
            contentElement: <CreateReelVideoSvg />,
        },
        {
            isStandIn: true,
            title: 'Upload an audio recording',
            text: 'Generate a Video Reel from a recording using the AI Stand-In feature.',
            bgClassName: 'group-hover:scale-75',
            contentClassName: 'group-hover:scale-110',
            bgElement: (
                <img
                    src="/img/shade-bg-e.svg"
                    style={{
                        transform: 'scale(2.6, 2.6) translate(0 , 0)',
                    }}
                    className="w-full h-full object-cover"
                />
            ),
            contentElement: <CreateReelAudioSvg />,
        },
        {
            isStandIn: true,
            title: 'Import or write a script',
            text: 'Generate a Video Reel from a script using the AI Stand-In feature.',
            bgClassName: 'group-hover:scale-125 scale-150',
            contentClassName: 'group-hover:scale-110',
            bgElement: (
                <img
                    src="/img/shade-bg-g.svg"
                    className="w-full h-full object-cover "
                />
            ),
            contentElement: <CreateReelScriptSvg className="translate-y-2" />,
        },
    ];

    return (
        <ModalView
            maxWidth="lg"
            onClose={onClose}
            contentContainerClassName="py-6 text-brand-ocean flex flex-col justify-center items-center">
            <div className="flex flex-row items-center w-full px-6">
                <div className="flex-1 text-heading-four !font-bold text-brand-ocean text-left">
                    Create Video Reel
                </div>
                <button
                    className="p-2 -mr-1 transition-all rounded-md hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            <div className="flex flex-col gap-y-3 mt-2 mx-4">
                {options.map((option, index) => {
                    return (
                        <div
                            aria-label={option.title}
                            tabIndex={0}
                            key={option.title}
                            className="group flex flex-row items-center hover:cursor-pointer hover:bg-neutral-mid px-2 py-2 transition-all duration-300 rounded-lg select-none">
                            <div className="relative min-w-36 min-h-20 rounded-md overflow-hidden flex items-center justify-center border border-gray-10">
                                <div
                                    className={classNames(
                                        'transition-all duration-300 flex-1 absolute w-full h-full',
                                        option.bgClassName,
                                    )}>
                                    {option.bgElement}
                                </div>
                                <div
                                    className={classNames(
                                        'transition-all duration-300 z-10',
                                        option.contentClassName,
                                    )}>
                                    {option.contentElement}
                                </div>
                            </div>
                            <div className="ml-4">
                                <div className="flex flex-row flex-wrap gap-x-2 items-center mb-2">
                                    <div className="text-body-text-15 text-brand-ocean">
                                        {option.title}
                                    </div>
                                    <div
                                        className={classNames(
                                            'py-1 px-2 text-small-text-10 rounded-full',
                                            {
                                                'opacity-0': !option.isStandIn,
                                            },
                                        )}
                                        style={{
                                            backgroundColor:
                                                'rgba(214, 206, 235, 1)',
                                            color: 'rgba(82, 39, 204, 1)',
                                        }}>
                                        AI Stand-In
                                    </div>
                                </div>
                                <div className="text-small-text-13 text-gray-80">
                                    {option.text}
                                </div>
                            </div>
                            <div className="ml-4 mr-2">
                                <ChevronRightSvg
                                    className="stroke-1 stroke-gray-80 opacity-80"
                                    width={10}
                                    height={10}
                                    fill="rgba(130, 130, 130, 1)"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModalView>
    );
};
